<template>
  <div class="col">
    <h3 class="text-center pt-3 pb-0 mb-0">{{ report.due_at | luxon:format('ccc MMM d, y') }}</h3>
    <span class="w-25 border-top d-block mx-auto my-2"></span>
    <p class="text-center">
      <small class="font-weight-bold text-uppercase">
        <span v-if="report.property">{{ report.property.label }}</span>
        <span v-else>{{ $t('labels.no_property') }}</span>
      </small>
    </p>
    <div v-if="report.events && report.events.length > 0" class="mb-3">
      <div class="d-flex justify-content-between">
        <div class="w-25 text-left"><small class="font-weight-bold text-uppercase">{{ $t('labels.arrived') }}</small></div>
        <div class="w-25 text-center"><small class="font-weight-bold text-uppercase">{{ secondsToHumanTime(billableTime) }}</small></div>
        <div class="w-25 text-right"><small class="font-weight-bold text-uppercase">{{ $t('labels.left') }}</small></div>
      </div>
      <div class="progress" style="height: 10px;">
        <div v-for="event in report.events"
             :key="'event' + event.id"
             :class="progressBarClass(event)"
             :style="'width: ' + progressBarWidth(event) + '%'"
             role="progressbar"></div>
      </div>
      <div class="d-flex justify-content-between">
        <div><small v-if="eventStart" class="font-weight-bold text-uppercase">{{ dt12Time(eventStart) }}</small></div>
        <div><small v-if="eventEnd" class="font-weight-bold text-uppercase">{{ dt12Time(eventEnd) }}</small></div>
      </div>
      <div>
        <div class="legend">
          <div>
            <div class="swatch bg-success"></div>
            <div class="label">Parked</div>
          </div>
          <div>
            <div class="swatch bg-warning"></div>
            <div class="label">Driving</div>
          </div>
          <div>
            <div class="swatch bg-warning-striped"></div>
            <div class="label">Driving more than 30 minutes (not included in total time)</div>
          </div>
        </div>
      </div>
    </div>
    <table class="table">
      <tr v-if="report.description">
        <td colspan="3">
          <p class="font-weight-bold text-uppercase mb-1">{{ $t('labels.description') }}:</p>
          {{ report.description }}
        </td>
      </tr>
      <tr v-for="task in report.tasks" :key="'task' + task.id">
        <td>{{ task.label }}</td>
        <td class="text-right"><span v-if="task.quantity">x{{ task.quantity }}</span></td>
        <td class="text-right text-success"><fa-icon v-if="task.complete" :icon="['fal', 'check']" /></td>
      </tr>
      <tr v-if="report.notes">
        <td colspan="3">
          <p class="font-weight-bold text-uppercase mb-1">{{ $t('labels.notes') }}:</p>
          {{ report.notes }}
        </td>
      </tr>
      <tr v-for="file in report.files" :key="'file' + file.id">
        <td colspan="3">
          <img :src="file.path" width="100%" /><br>
          <p><small>Photo Taken: {{ file.created_at | luxon:format('ccc, MMM d, y h:mma') }}</small></p>
        </td>
      </tr>
      <tfoot>
        <tr>
          <td colspan="3" class="text-center">
            <bs-button class="btn-secondary" @click="create()">Email Report</bs-button>
          </td>
        </tr>
      </tfoot>
    </table>
    <bs-modal :name="'create-' + $options.name" :label="$t('labels.action_resource', { action: $t('labels.email'), resource: $t('labels.report', {}, 1) })">
      <bs-form :submit="$t('labels.submit')" @submit="submit">
        <template>
          <multiselect class="mb-3" v-model="resource.contact" :options="filteredContacts" :multiple="false" :close-on-select="true" :clear-on-select="false" :preserve-search="false" :placeholder="$t('labels.choose_option', { resource: $t('labels.contact') })" label="name" track-by="id">
            <template slot="option" slot-scope="props">
              <div class="option__desc">
                <span class="option__title">{{ props.option.name }}</span><br>
                <span class="option__small"><small>({{ props.option.email }})</small></span>
              </div>
            </template>
          </multiselect>
        </template>
      </bs-form>
    </bs-modal>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { mapState } from 'vuex'

export default {
  name: 'BookingReport',
  components: {
    Multiselect
  },
  props: ['report'],
  data: function () {
    return {
      resource: {
        contact: null
      }
    }
  },
  mounted () {
    this.$store.dispatch('index', { collection: 'allContacts' })
  },
  computed: {
    ...mapState({
      users: state => state.usersStore,
      contacts: state => state.allContacts.data
    }),
    filteredContacts () {
      const contacts = this._.filter(this.contacts, (c) => { return c.email && c.customer_id === this.report.customer_id })
      return contacts.concat(this.users)
    },
    eventStart () {
      const event = this._.orderBy(this.report.events, ['from'], ['asc']).shift()
      if (event) return event.from
      return null
    },
    eventEnd () {
      const event = this._.orderBy(this.report.events, ['to'], ['desc']).shift()
      if (event) return event.to
      return null
    },
    time () {
      let time = 0
      this.report.events.forEach((e) => { time += e.time })
      return time
    },
    billableTime () {
      let time = 0
      this.report.events
        .filter(e => e.status === 'parked' || (e.status === 'trip' && e.time < 1800))
        .forEach(e => {
          time += e.time
        })
      return time
    }
  },
  methods: {
    progressBarClass (event) {
      let bg = 'bg-success'
      let str = ''
      if (event.status === 'trip') bg = 'bg-warning'
      if (event.time > 1800 && event.status === 'trip') str = 'progress-bar-striped '
      return 'progress-bar ' + str + bg
    },
    progressBarWidth (event) {
      console.log(event)
      console.log((event.time / this.time) * 100)
      return (event.time / this.time) * 100
    },
    customLabel ({ name, email }) {
      return `${name} <small>(${email})</small>`
    },
    create () {
      this.$store.commit('axiosError', null)
      this.$modal.show('create-' + this.$options.name)
    },
    submit () {
      this.$store.dispatch('store', { collection: this.$options.name, id: this.report.id, resource: this.resource.contact }).then(() => {
        this.$modal.hide('create-' + this.$options.name)
      })
    }
  }
}
</script>
